"use client"

import { useState, useEffect } from "react"
import { collection, addDoc, getDocs, deleteDoc, doc, serverTimestamp, query, orderBy, updateDoc } from "firebase/firestore"
import { ref, uploadBytes, getDownloadURL } from "firebase/storage"
import { db, storage } from "@/lib/firebase"
import { Plus, Trash2, Loader2, UploadCloud, UserCircle, Edit2, CheckCircle2, MessageSquareQuote, Eye, EyeOff, Image as ImageIcon, Star } from "lucide-react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import MediaLibrary from "./media-library"
import { toast } from "sonner"
import { motion } from "framer-motion"

export default function TestimonialsManager() {
  const [testimonials, setTestimonials] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [editingTestimonial, setEditingTestimonial] = useState<any>(null)
  const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false)

  // Form State
  const [name, setName] = useState("")
  const [designation, setDesignation] = useState("")
  const [category, setCategory] = useState("Review")
  const [content, setContent] = useState("")
  const [portal, setPortal] = useState("tspsc")
  const [rank, setRank] = useState("")
  const [marks, setMarks] = useState("")
  const [service, setService] = useState("Group I Services")
  const [rating, setRating] = useState(5)
  const [imageFile, setImageFile] = useState<File | null>(null)

  useEffect(() => {
    fetchTestimonials()
  }, [])

  const fetchTestimonials = async () => {
    try {
      const q = query(collection(db, "testimonials"), orderBy("createdAt", "desc"))
      const snapshot = await getDocs(q)
      setTestimonials(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })))
    } catch (error) {
      console.error(error)
      toast.error("Failed to load testimonials")
    } finally {
      setIsLoading(false)
    }
  }

  const resetForm = () => {
    setName("")
    setDesignation("")
    setCategory("Review")
    setContent("")
    setPortal("tspsc")
    setRank("")
    setMarks("")
    setService("Group I Services")
    setRating(5)
    setImageFile(null)
    setEditingTestimonial(null)
    setShowForm(false)
  }

  const handleEdit = (t: any) => {
    setEditingTestimonial(t)
    setName(t.name || "")
    setDesignation(t.designation || "")
    setCategory(t.category || "Review")
    setContent(t.content || "")
    setPortal(t.portal || "tspsc")
    setRank(t.rank || "")
    setMarks(t.marks || "")
    setService(t.service || "Group I Services")
    setRating(t.rating || 5)
    setShowForm(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name || (category === 'Review' && !content)) {
      toast.error("Name and content are required")
      return
    }

    setIsSubmitting(true)
    try {
      let imageUrl = editingTestimonial?.imageUrl || ""
      if (imageFile) {
        const imgRef = ref(storage, `testimonials/${Date.now()}_${imageFile.name}`)
        await uploadBytes(imgRef, imageFile)
        imageUrl = await getDownloadURL(imgRef)
        
        await addDoc(collection(db, "media"), {
           title: `${name} Testimonial Photo`,
           url: imageUrl,
           type: "image",
           folder: "images",
           createdAt: serverTimestamp(),
        })
      }

      const data = {
        name: name || "",
        designation: designation || "",
        category: category || "Review",
        content: content || "",
        portal: portal || "tspsc",
        rank: rank || "",
        marks: marks || "",
        service: service || "",
        imageUrl: imageUrl || "",
        rating: rating || 5,
        updatedAt: serverTimestamp(),
        createdAt: (editingTestimonial && editingTestimonial.id) ? editingTestimonial.createdAt : serverTimestamp(),
        isVisible: true
      }

      if (editingTestimonial?.id) {
        const existing = testimonials.find(t => t.id === editingTestimonial.id)
        await updateDoc(doc(db, "testimonials", editingTestimonial.id), {
          ...data,
          isVisible: existing?.isVisible !== undefined ? existing.isVisible : true
        })
        toast.success("Updated successfully")
      } else {
        await addDoc(collection(db, "testimonials"), data)
        toast.success("Published successfully")
      }

      resetForm()
      fetchTestimonials()
    } catch (error) {
      console.error(error)
      toast.error("Operation failed")
    } finally {
      setIsSubmitting(false)
    }
  }

  const toggleVisibility = async (testimonial: any) => {
    try {
      const newStatus = !testimonial.isVisible
      await updateDoc(doc(db, "testimonials", testimonial.id), { isVisible: newStatus })
      toast.success(newStatus ? "Story visible" : "Story hidden")
      setTestimonials(prev => prev.map(t => t.id === testimonial.id ? { ...t, isVisible: newStatus } : t))
    } catch (error) {
      toast.error("Failed to update visibility")
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm("Permanently delete this testimonial?")) return
    try {
      await deleteDoc(doc(db, "testimonials", id))
      toast.success("Deleted")
      fetchTestimonials()
    } catch (error) {
      toast.error("Failed to delete")
    }
  }

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* Header Area */}
      <div className="flex justify-between items-center bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
        <div>
          <h2 className="text-2xl font-serif font-bold text-slate-900">Testimonials & Reviews</h2>
          <p className="text-slate-500 text-sm">Manage student success stories and institutional reviews.</p>
        </div>
        <button 
          onClick={() => {
            if (showForm) resetForm()
            else setShowForm(true)
          }}
          className="bg-slate-900 text-white px-6 py-3 rounded-xl flex items-center gap-2 hover:bg-[#b48c36] transition-all font-bold text-xs uppercase tracking-widest shadow-lg shadow-slate-200"
        >
          {showForm ? "Cancel" : <><Plus size={18}/> Add Testimonial</>}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white p-8 rounded-3xl shadow-2xl border border-slate-100 space-y-8 max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Full Name</label>
                <input 
                  value={name} onChange={(e) => setName(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 outline-none focus:border-[#b48c36] transition-all"
                  placeholder="e.g. Naga Maruthi"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Role/Designation</label>
                <input 
                  value={designation} onChange={(e) => setDesignation(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 outline-none focus:border-[#b48c36] transition-all"
                  placeholder="e.g. Student of HIAS / CSAT Faculty"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Category</label>
                <select 
                  value={category} onChange={(e) => setCategory(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 outline-none focus:border-[#b48c36] transition-all"
                >
                  <option value="Review">Review</option>
                  <option value="Success Story">Success Story</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Rating</label>
                <div className="flex gap-2 bg-slate-50 border border-slate-100 rounded-xl px-4 py-3">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setRating(star)}
                      className="focus:outline-none transition-transform hover:scale-110"
                    >
                      <Star
                        size={24}
                        className={`${
                          star <= rating ? "fill-amber-400 text-amber-400" : "text-slate-300"
                        } transition-colors`}
                      />
                    </button>
                  ))}
                  <span className="ml-2 text-slate-400 font-bold text-sm self-center">{rating}/5</span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Profile Picture</label>
              <div className="relative group flex flex-col items-center justify-center border-2 border-dashed border-slate-200 rounded-2xl h-[230px] bg-slate-50 hover:bg-slate-100 transition-all overflow-hidden">
                {imageFile ? (
                  <div className="text-center p-4">
                     <CheckCircle2 className="mx-auto text-emerald-500 mb-2" size={32} />
                     <p className="text-xs font-bold text-slate-600 truncate max-w-[200px]">{imageFile.name}</p>
                     <button type="button" onClick={() => setImageFile(null)} className="text-red-500 text-[10px] font-bold uppercase mt-2">Remove</button>
                  </div>
                ) : editingTestimonial?.imageUrl ? (
                  <div className="relative w-full h-full">
                     <img src={editingTestimonial.imageUrl} className="w-full h-full object-cover" alt="" />
                     <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <p className="text-white text-[10px] font-bold uppercase tracking-widest">Update Photo</p>
                     </div>
                  </div>
                ) : (
                  <div className="text-center p-4">
                     <UserCircle className="mx-auto text-slate-300 mb-2 group-hover:text-[#b48c36] transition-colors" size={48} />
                     <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Upload Profile Pic</p>
                  </div>
                )}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-3 p-4">
                   <label className="cursor-pointer text-white text-[10px] font-bold uppercase tracking-widest border border-white px-4 py-2 hover:bg-white hover:text-black transition-colors w-full text-center">
                      Upload File
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => setImageFile(e.target.files?.[0] || null)} />
                   </label>
                   <button 
                     type="button"
                     onClick={() => setIsMediaLibraryOpen(true)}
                     className="text-white text-[10px] font-bold uppercase tracking-widest border border-white px-4 py-2 hover:bg-white hover:text-black transition-colors w-full">
                      From Library
                   </button>
                </div>
              </div>

              <Dialog open={isMediaLibraryOpen} onOpenChange={setIsMediaLibraryOpen}>
                 <DialogContent className="w-[95vw] max-w-5xl p-0 overflow-hidden border-none bg-white">
                   <DialogHeader className="p-5 sm:p-6 bg-background border-b border-border/30">
                     <DialogTitle>Select from Media Library</DialogTitle>
                   </DialogHeader>
                   <MediaLibrary
                     allowSelection
                     initialTab="images"
                     onSelect={(media) => {
                        setEditingTestimonial({ ...editingTestimonial, imageUrl: media.url })
                        setImageFile(null)
                        setIsMediaLibraryOpen(false)
                     }}
                   />
                 </DialogContent>
              </Dialog>
            </div>
          </div>

          {category === 'Success Story' && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 p-6 bg-slate-50 rounded-2xl border border-slate-100"
            >
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Portal</label>
                <select 
                  value={portal} onChange={(e) => setPortal(e.target.value)}
                  className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-[#b48c36] transition-all"
                >
                  <option value="tspsc">TSPSC</option>
                  <option value="appc">APPSC</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Rank</label>
                <input 
                  value={rank} onChange={(e) => setRank(e.target.value)}
                  className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-[#b48c36] transition-all"
                  placeholder="01 / 15 / etc."
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Marks</label>
                <input 
                  value={marks} onChange={(e) => setMarks(e.target.value)}
                  className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-[#b48c36] transition-all"
                  placeholder="892 / etc."
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Service</label>
                <input 
                  value={service} onChange={(e) => setService(e.target.value)}
                  className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-[#b48c36] transition-all"
                  placeholder="Group I Services"
                />
              </div>
            </motion.div>
          )}

          <div className="space-y-2">
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Testimonial Content</label>
            <textarea 
              value={content} onChange={(e) => setContent(e.target.value)}
              rows={5}
              className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-4 py-4 outline-none focus:border-[#b48c36] transition-all resize-none"
              placeholder="Write the quote or success story here..."
            />
          </div>

          <button 
            type="submit" 
            disabled={isSubmitting}
            className="w-full bg-slate-900 text-white py-4 rounded-xl font-bold uppercase tracking-widest text-xs flex items-center justify-center gap-3 disabled:opacity-50 hover:bg-[#b48c36] transition-all shadow-xl shadow-slate-200"
          >
            {isSubmitting ? <><Loader2 className="animate-spin" /> Processing...</> : (editingTestimonial ? "Update Story" : "Publish Story")}
          </button>
        </form>
      )}

      {/* Grid Display - Matches Reference UI */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {isLoading ? (
          <div className="col-span-full py-20 flex flex-col items-center gap-4">
             <Loader2 className="animate-spin text-[#b48c36]" />
             <p className="text-[10px] font-bold uppercase tracking-[0.4em] text-slate-400">Syncing Testimonials</p>
          </div>
        ) : testimonials.length === 0 ? (
          <div className="col-span-full py-20 text-center">
             <MessageSquareQuote className="mx-auto text-slate-100 mb-4" size={64} />
             <p className="text-slate-400 font-serif italic text-lg">No success stories found.</p>
          </div>
        ) : (
          testimonials.map((t) => (
            <div key={t.id} className="relative group bg-white border border-slate-100 rounded-3xl p-6 shadow-sm hover:shadow-xl transition-all duration-300">
               {/* Metadata header */}
               <div className="flex items-center justify-between mb-6">
                  <span className={`text-[8px] font-bold uppercase tracking-[0.2em] px-3 py-1.5 rounded-full ${t.category === 'Success Story' ? 'bg-orange-100 text-orange-600' : 'bg-slate-900 text-[#b48c36]'}`}>
                     {t.category}
                  </span>
                  <div className="flex items-center gap-2">
                     <button onClick={() => handleEdit(t)} className="p-2 text-slate-300 hover:text-[#b48c36] transition-colors" title="Edit"><Edit2 size={16} /></button>
                     <button onClick={() => toggleVisibility(t)} className={`p-2 transition-colors ${t.isVisible === false ? 'text-rose-500' : 'text-slate-300 hover:text-emerald-500'}`} title={t.isVisible === false ? "Hidden" : "Live"}>{t.isVisible === false ? <EyeOff size={16} /> : <Eye size={16} />}</button>
                     <button onClick={() => handleDelete(t.id)} className="p-2 text-slate-300 hover:text-red-500 transition-colors" title="Delete"><Trash2 size={16} /></button>
                  </div>
               </div>

               {/* Profile Info */}
               <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-full overflow-hidden bg-slate-100 shrink-0 border border-slate-200">
                     {t.imageUrl ? (
                        <img src={t.imageUrl} className="w-full h-full object-cover" alt="" />
                     ) : (
                        <div className="w-full h-full flex items-center justify-center text-[#b48c36] font-serif font-bold italic">
                           {t.name?.charAt(0)}
                        </div>
                     )}
                  </div>
                  <div>
                     <h4 className="font-bold text-slate-950 tracking-tight leading-tight">{t.name}</h4>
                     <p className="text-[10px] text-slate-400 uppercase tracking-widest mt-0.5">{t.designation}</p>
                  </div>
               </div>

               {/* Rating & Content */}
               <div className="relative">
                  <div className="flex gap-0.5 mb-3">
                     {[1, 2, 3, 4, 5].map((star) => (
                        <Star key={star} size={12} className={star <= (t.rating || 5) ? "fill-amber-400 text-amber-400" : "text-slate-200"} />
                     ))}
                  </div>
                  <p className="text-slate-600 text-sm leading-relaxed italic line-clamp-4">"{t.content}"</p>
               </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
